import type { GridApi } from "ag-grid-community";

export interface GridSelectionHelpers<TId extends string | number, TData> {
  collectLoadedRowIds: (api: GridApi<TData>) => TId[];
  collectCurrentPageRowIds: (api: GridApi<TData>) => TId[];
  collectLoadedSelectedRowIds: (api: GridApi<TData>, limit: number) => TId[];
}

export const createGridSelectionHelpers = <TId extends string | number, TData>(
  getRowId: (data: TData | undefined) => TId | null,
): GridSelectionHelpers<TId, TData> => {
  const collectLoadedRowIds = (api: GridApi<TData>): TId[] => {
    const ids: TId[] = [];
    api.forEachNode(node => {
      const id = getRowId(node.data);
      if (id !== null) {
        ids.push(id);
      }
    });
    return ids;
  };

  const collectCurrentPageRowIds = (api: GridApi<TData>): TId[] => {
    const pageSize = api.paginationGetPageSize();
    const currentPage = api.paginationGetCurrentPage();
    const startRow = currentPage * pageSize;
    const endRow = startRow + pageSize;
    const ids: TId[] = [];

    api.forEachNode(node => {
      if (node.rowIndex === null || node.rowIndex < startRow || node.rowIndex >= endRow) {
        return;
      }
      const id = getRowId(node.data);
      if (id !== null) {
        ids.push(id);
      }
    });

    return ids;
  };

  const collectLoadedSelectedRowIds = (api: GridApi<TData>, limit: number): TId[] => {
    const ids: TId[] = [];
    api.forEachNode(node => {
      if (node.rowIndex === null || node.rowIndex >= limit) {
        return;
      }
      const id = getRowId(node.data);
      if (id !== null) {
        ids.push(id);
      }
    });
    return ids;
  };

  return {
    collectLoadedRowIds,
    collectCurrentPageRowIds,
    collectLoadedSelectedRowIds,
  };
};
